import { create } from 'zustand';
import { spacing } from '@/theme/tokens';

/**
 * One-shot trigger for the sign-in intro, plus the small contract shared by the
 * two sides of it: the login page marks up what can be measured, and
 * `SignInHandoff` reproduces it.
 *
 * Deliberately NOT persisted. `auth.store` puts `isAuthenticated` through
 * `partialize`, so "the user is authenticated" is true again after a hard
 * refresh, an HMR reload, or a restored tab — keying the animation off that
 * would replay it every time. The trigger has to be the *transition*, so it
 * lives in memory only and is cleared the moment it has been used.
 */

const PANEL_ATTR = 'data-signin-panel';
const MARK_ATTR = 'data-signin-mark';
const COPY_ATTR = 'data-signin-copy';
const RAIL_LOGO_ATTR = 'data-rail-logo';

/**
 * Spread onto the login page's dark panel, brand tile and headline. Handing out
 * the props rather than the attribute names keeps the markup and the selectors
 * below reading the same constants, so they cannot drift apart.
 */
export const signInPanelProps = { [PANEL_ATTR]: '' };
export const signInMarkProps = { [MARK_ATTR]: '' };
export const signInCopyProps = { [COPY_ATTR]: '' };

/**
 * Spread onto the rail's brand tile in `main-layout.tsx`, and nowhere else. The
 * overlay polls for it to tell whether the shell has actually painted.
 */
export const railLogoProps = { [RAIL_LOGO_ATTR]: '' };
export const RAIL_LOGO_SELECTOR = `[${RAIL_LOGO_ATTR}]`;

/**
 * The login panel's copy, kept here because the overlay has to redraw it after
 * `LoginPage` has already unmounted. Both sides read these, so they cannot
 * drift apart into a visible jump at the start of the animation.
 */
export const SIGN_IN_HEADLINE = 'Manage every device from one console.';
export const SIGN_IN_SUBHEAD = 'Provision, monitor, and audit fleets across regions.';

/**
 * Which intro a sign-in gets.
 *
 * `handoff` is the everyday one: the login panel narrows into the rail, inside
 * time the app is already spending on its landing chunk. `splash` is the brand
 * moment, and it is rationed — see `SPLASH_AFTER_MS`. A splash that plays on
 * every sign-in stops covering the wait and becomes the wait; one that plays
 * after a real absence is a greeting.
 */
export type SignInIntroMode = 'handoff' | 'splash';

export interface SignInHandoffFrom {
  /** Width of the login page's dark left panel, in px. */
  panelW: number;
  /** Viewport rect of the orange brand tile. Square, so one side is enough. */
  mark: { x: number; y: number; size: number };
  /** Viewport position and width of the headline. */
  copy: { x: number; y: number; w: number };
}

export interface SignInIntro {
  mode: SignInIntroMode;
  /**
   * Login-page geometry. Only the handoff needs it — the splash brings its own
   * ground and has nothing to travel from.
   */
  from: SignInHandoffFrom | null;
}

/** Where the last visit is remembered. Per browser, which is the right scope:
 *  this is about how familiar *this* screen is, not about the account. */
const LAST_SEEN_KEY = 'aviron-mdm:last-seen';

/** Dev/QC only — see `queueIntroOnNextLoad`. sessionStorage, so it survives one
 *  reload of this tab and nothing else. */
const PENDING_KEY = 'aviron-mdm:intro-pending';

/**
 * How long an absence has to be before the next sign-in earns the splash.
 *
 * Two days means a daily user never sees it and someone back from a weekend
 * sees it once. Turn it down to try it out; a value near zero turns the splash
 * back into the per-login tax it is rationed to avoid.
 */
export const SPLASH_AFTER_MS = 2 * 24 * 60 * 60 * 1000;

interface SignInHandoffState {
  intro: SignInIntro | null;
  /**
   * Bumped on every arm. The overlay uses it as a React key so a second sign-in
   * in one page life gets a fresh run rather than having to reset its own state
   * from inside an effect.
   */
  armId: number;
  arm: (intro: SignInIntro) => void;
  disarm: () => void;
}

export const useSignInHandoffStore = create<SignInHandoffState>((set) => ({
  intro: null,
  armId: 0,
  arm: (intro) => set((s) => ({ intro, armId: s.armId + 1 })),
  disarm: () => set({ intro: null }),
}));

/**
 * Stamps "the app was used just now". Called from `MainLayout` on mount, so the
 * clock measures time away from the console rather than time between sign-ins —
 * someone who works in here all week and then re-authenticates has not been
 * away, and should not be greeted as though they had.
 */
export function markAccess(): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
  } catch {
    // Private mode, or storage disabled. Nothing to remember, so the next
    // sign-in falls back to the everyday handoff.
  }
}

/**
 * Forgets the last visit, so the next real sign-in is treated as a first one.
 * Dev/QC entry point behind `resetSignInIntro()`.
 */
export function forgetLastAccess(): void {
  try {
    localStorage.removeItem(LAST_SEEN_KEY);
  } catch {
    // Nothing stored, nothing to forget.
  }
}

/**
 * Queues an intro to play on the next page load of this tab.
 *
 * The only path that fires an intro on a load rather than a sign-in, and it
 * exists purely so the animations can be iterated on with a refresh instead of
 * a sign-out/sign-in round trip. Safe because it fires only for a key a console
 * helper wrote by hand, and `consumeQueuedIntro` removes that key before arming.
 */
export function queueIntroOnNextLoad(mode: SignInIntroMode): void {
  try {
    sessionStorage.setItem(PENDING_KEY, mode);
  } catch {
    // Private mode. The reload just will not replay anything.
  }
}

/**
 * Plays a queued intro, once. Called at app start-up.
 *
 * One-shot by construction: the key is read and removed in the same breath, so
 * a second refresh shows nothing. That matters more than it looks — an intro
 * that survives repeated reloads is exactly the bug the whole trigger design is
 * built to avoid.
 */
export function consumeQueuedIntro(): void {
  let mode: string | null = null;
  try {
    mode = sessionStorage.getItem(PENDING_KEY);
    if (mode) sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  if (mode === 'splash' || mode === 'handoff') armSignInHandoffPreview(mode);
}

function pickMode(): SignInIntroMode {
  let stamp: string | null = null;
  try {
    stamp = localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    // Without somewhere to remember visits, every sign-in would look like a
    // first visit. The everyday handoff is the safer default.
    return 'handoff';
  }
  if (!stamp) return 'splash'; // never been here on this browser
  const last = Number(stamp);
  if (!Number.isFinite(last)) return 'splash';
  return Date.now() - last > SPLASH_AFTER_MS ? 'splash' : 'handoff';
}

function measureLoginPanel(): SignInHandoffFrom | null {
  const panel = document.querySelector(`[${PANEL_ATTR}]`);
  const mark = document.querySelector(`[${MARK_ATTR}]`);
  const copy = document.querySelector(`[${COPY_ATTR}]`);
  if (!panel || !mark || !copy) return null;

  const p = panel.getBoundingClientRect();
  const m = mark.getBoundingClientRect();
  const c = copy.getBoundingClientRect();

  // `display: none` collapses a rect to zero, and a panel no wider than the
  // rail has nowhere to travel from either way.
  if (m.width === 0 || p.width <= spacing.sidebarWidth) return null;

  return {
    panelW: p.width,
    mark: { x: m.left, y: m.top, size: m.width },
    copy: { x: c.left, y: c.top, w: c.width },
  };
}

/**
 * Picks an intro and arms it.
 *
 * Call it immediately before `navigate('/')` on a real sign-in, while the login
 * DOM is still mounted — and only on a real sign-in. The "redirect if already
 * authenticated" effect in `LoginPage` also navigates to `/`, but that is a
 * bounce off a restored session rather than a transition, and animating it
 * would be the same mistake as keying off `isAuthenticated`.
 *
 * Returns false and arms nothing when the handoff was chosen but the branding
 * panel is not on screen: below the `md` breakpoint it is `display: none`, and
 * there is nothing to hand off from. Callers can ignore the result — a false
 * just means the app behaves exactly as it did before.
 */
export function armSignInHandoff(): boolean {
  if (typeof document === 'undefined') return false;

  const mode = pickMode();

  // The splash draws its own full-bleed ground, so it needs no measurement and
  // works below the md breakpoint where the login panel is not rendered.
  if (mode === 'splash') {
    useSignInHandoffStore.getState().arm({ mode, from: null });
    return true;
  }

  const from = measureLoginPanel();
  if (!from) return false;
  useSignInHandoffStore.getState().arm({ mode, from });
  return true;
}

/**
 * Arms an intro from computed geometry instead of a measurement, so either one
 * can be replayed from anywhere in the app.
 *
 * This exists because `VITE_AUTH_BYPASS=true` authenticates straight out of
 * `initBypassMode()` and never renders the login page — local dev would
 * otherwise never see these animations at all, and the splash in particular is
 * rationed to roughly never. The numbers below reproduce the login panel's
 * bottom-anchored stack (56px padding, a 40px tile, a 40px gap, a two-line
 * 40px/1.15 headline, a 16px gap and a one-line subhead), which makes it an
 * approximation good enough to eyeball motion against. A real sign-in measures
 * the real thing.
 */
export function armSignInHandoffPreview(mode: SignInIntroMode = 'handoff'): void {
  if (mode === 'splash') {
    useSignInHandoffStore.getState().arm({ mode, from: null });
    return;
  }

  const vh = window.innerHeight;
  const PAD = 56;
  const SUBHEAD_H = 25; // 16px at 1.55 line-height, one line
  const HEADLINE_H = 92; // 40px at 1.15, two lines
  const HEADLINE_GAP = 16; // mb: 2
  const LOGO_GAP = 40; // mb: 5
  const MARK = 40;

  const copyY = vh - PAD - SUBHEAD_H - HEADLINE_GAP - HEADLINE_H;

  useSignInHandoffStore.getState().arm({
    mode,
    from: {
      panelW: window.innerWidth * 0.65,
      mark: { x: PAD, y: copyY - LOGO_GAP - MARK, size: MARK },
      copy: { x: PAD, y: copyY, w: 520 },
    },
  });
}
